import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Button,
} from '@mui/material';
import { IconChevronLeft, IconChevronRight, IconPrinter } from '@tabler/icons-react';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import useContractPermissions from 'hooks/useContractPermissions';

const months = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' },
];

const currentYear = new Date().getFullYear();
const minYear = currentYear - 4;
const years = Array.from({ length: 5 }, (_, i) => ({
  value: currentYear - i,
  label: String(currentYear - i),
}));

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const TETO_INSS_COMMISSION = 10;

const formatValuePerContract = (row) => {
  const tetoCount = row.teto_inss_count || 0;
  const otherCount = (row.total_contracts || 0) - tetoCount;
  if (tetoCount > 0 && otherCount > 0 && Number(row.value_per_contract) !== TETO_INSS_COMMISSION) {
    return `${formatCurrency(row.value_per_contract)} · TETO INSS ${formatCurrency(TETO_INSS_COMMISSION)}`;
  }
  if (tetoCount > 0 && otherCount === 0) {
    return formatCurrency(TETO_INSS_COMMISSION);
  }
  return formatCurrency(row.value_per_contract);
};

const CommissionsReport = () => {
  const { canViewCommissionReports, isSuperuser, loading: permissionsLoading } = useContractPermissions();
  const canAccess = isSuperuser || canViewCommissionReports;

  const currentMonth = new Date().getMonth() + 1;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState({ data: [], total_contracts: 0, total_value: 0 });

  const isCurrentMonthYear = selectedMonth === currentMonth && selectedYear === currentYear;
  const isFirstMonthYear = selectedMonth === 1 && selectedYear === minYear;

  const handlePreviousMonth = () => {
    if (isFirstMonthYear) return;
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (isCurrentMonthYear) return;
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const fetchReport = useCallback(async () => {
    if (!canAccess) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `/backend/reports/commissions-by-sales-consultant/?year=${selectedYear}&month=${selectedMonth}`
      );
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Você não tem permissão para visualizar este relatório.');
        }
        throw new Error('Erro ao carregar o relatório de comissões.');
      }
      setReportData(await response.json());
    } catch (err) {
      setError(err.message);
      setReportData({ data: [], total_contracts: 0, total_value: 0 });
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, canAccess]);

  useEffect(() => {
    if (!permissionsLoading && canAccess) {
      fetchReport();
    }
  }, [fetchReport, permissionsLoading, canAccess]);

  const monthLabel = months.find((m) => m.value === selectedMonth)?.label || '';

  const handlePrint = () => {
    window.print();
  };

  if (permissionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canAccess) {
    return (
      <MainCard title="Comissões dos consultores">
        <Alert severity="warning">Você não tem permissão para visualizar este relatório.</Alert>
      </MainCard>
    );
  }

  return (
    <Grid container spacing={gridSpacing}>
      <Grid item xs={12} className="no-print">
        <MainCard>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton onClick={handlePreviousMonth} size="small" color="primary" disabled={isFirstMonthYear}>
                <IconChevronLeft size={20} />
              </IconButton>
              <TextField
                select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                sx={{ minWidth: 130 }}
                size="small"
              >
                {months.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                sx={{ minWidth: 100 }}
                size="small"
              >
                {years.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton onClick={handleNextMonth} size="small" color="primary" disabled={isCurrentMonthYear}>
                <IconChevronRight size={20} />
              </IconButton>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<IconPrinter size={18} />}
              onClick={handlePrint}
              disabled={loading || reportData.data.length === 0}
            >
              Imprimir
            </Button>
          </Box>
        </MainCard>
      </Grid>

      <Grid item xs={12}>
        <MainCard title={`Comissões dos consultores — ${monthLabel}/${selectedYear}`}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : reportData.data.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
              Nenhum dado encontrado para o período selecionado
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Consultor de vendas</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>NORMAL</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>RCC</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>RMC</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>TETO INSS</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Total contratos</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor por contrato</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.data.map((row) => (
                      <TableRow key={row.sales_consultant_id} hover>
                        <TableCell>{row.sales_consultant_name}</TableCell>
                        <TableCell align="center">{row.normal_count}</TableCell>
                        <TableCell align="center">{row.rcc_count}</TableCell>
                        <TableCell align="center">{row.rmc_count}</TableCell>
                        <TableCell align="center">{row.teto_inss_count}</TableCell>
                        <TableCell align="center">{row.total_contracts}</TableCell>
                        <TableCell align="right">{formatValuePerContract(row)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.total_value)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {reportData.data.reduce((sum, row) => sum + row.normal_count, 0)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {reportData.data.reduce((sum, row) => sum + row.rcc_count, 0)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {reportData.data.reduce((sum, row) => sum + row.rmc_count, 0)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {reportData.data.reduce((sum, row) => sum + (row.teto_inss_count || 0), 0)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {reportData.total_contracts}
                      </TableCell>
                      <TableCell align="right" />
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(reportData.total_value)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2 }} className="no-print">
                <Typography variant="caption" color="textSecondary">
                  Valor por contrato: R$ 20,00 quando há ao menos 1 contrato NORMAL (inclui NORMAL COM PRESTAMISTA e PRESTAMISTA); R$ 10,00 quando há somente RCC e/ou RMC. TETO INSS é sempre R$ 10,00, mesmo quando os demais contratos do consultor estão em R$ 20,00.
                  Contagem baseada em contratos válidos (exclui CANCELADO, FALTAM DOCUMENTOS e NÃO POSSUI ABUSIVIDADE).
                </Typography>
              </Box>
            </>
          )}
        </MainCard>
      </Grid>

      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            body * {
              visibility: hidden;
            }
            #root, #root * {
              visibility: visible;
            }
            .MuiDrawer-root,
            .MuiAppBar-root,
            nav {
              display: none !important;
            }
            main {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
            }
          }
        `}
      </style>
    </Grid>
  );
};

export default CommissionsReport;
